/**
 * EAA-1878: a plain helper, not a component — split out of
 * `AppDetailAccountsTab.tsx` for the same reason `accounts/shared/cellHelpers.ts`
 * was split out of `cells.tsx`: react-refresh's `only-export-components` rule
 * flags a `.tsx` that mixes the two, and a hot reload then invalidates the whole
 * module instead of patching the component.
 */

/**
 * portal-api's user-sync placeholder `Level.role_key` — the sentinel meaning
 * "synced from an app, no role mapped yet", never a role a person authored.
 *
 * It used to be the bare `'unassigned'`, ONE shared row per Organization whichever
 * app synced it, which is precisely why every app's accounts tab listed the same
 * accounts. It is now `unassigned-<app code>`, minted per (Organization,
 * Application) by `UserSyncService.placeholder_role_key`. Both shapes are matched:
 * the backfill migration deliberately leaves the bare key on Organizations no
 * application claims, and such a row must still render as "no role assigned"
 * rather than leaking a raw sentinel into the UI.
 *
 * NULL IS ACCEPTED, and that is the whole reason this is a named, tested helper.
 * `role_key` is nullable — `outcomes_for_app` derives it from `row.level`, and a
 * DIRECT grant has no Level — so a response whose rows are all direct grants sends
 * `role_key: null` for every one of them. Calling `.startsWith()` on that
 * white-screened the entire accounts tab on dev staging, and `strict` is false in
 * this app's tsconfig so nothing warned. Null means "no standard role to show",
 * which is the same thing the caller wants to say for the sentinel.
 */
export function isUnassignedRoleKey(roleKey: string | null | undefined): boolean {
    if (!roleKey) return true;
    return roleKey === 'unassigned' || roleKey.startsWith('unassigned-');
}
